import React, { useCallback, useEffect, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { depositText } from '../i18n/pages/deposit';
import { apartmentEditNavText } from '../i18n/pages/apartmentEditNav';
import { AdvancedDetails } from './AdvancedDetails';
import {
  DepositSettings as Settings,
  getDepositSettings,
  updateDepositSettings,
} from '../api.deposit';

interface Props {
  apartmentId: number;
  /** Called after a change was accepted by the server, so the page's summary line can refresh. */
  onSaved?: () => void;
}

/**
 * Per-apartment deposit configuration — five fields, which is the fewest that still express a real
 * policy.
 *
 * <p>Two of them are worth reading twice. <b>Reserve it N days before check-in</b> exists because a
 * card authorization lapses after about a week, so a deposit cannot be taken at booking; it has to
 * be timed around the stay. And <b>if you don't decide</b> is not a choice between safe and unsafe:
 * both settings end with the guest's money back, because the hold expires either way. The only
 * difference is whether that was a decision or a lapse.
 */
const DepositSettings: React.FC<Props> = ({ apartmentId, onSaved }) => {
  const { lang } = useLang();
  const t = depositText[lang];
  const advancedTitle = apartmentEditNavText[lang].advanced;

  const [settings, setSettings] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSettings(await getDepositSettings(apartmentId));
    } catch {
      setSettings(null);
    }
  }, [apartmentId]);

  useEffect(() => { void load(); }, [load]);

  const patch = async (body: Partial<Settings>) => {
    setSaving(true);
    setError(null);
    try {
      setSettings(await updateDepositSettings(apartmentId, body));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      onSaved?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || t.actionError);
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  const money = (v: number | null) =>
    v === null ? '' : `${v.toLocaleString(lang === 'en' ? 'en-GB' : lang)} ${settings.currency}`;

  // A deposit can never be free, so an amount is as much a precondition as a Stripe account.
  const needsAmount = (settings.amount ?? 0) <= 0;
  const canEnable = settings.paymentsReady && !needsAmount;

  return (
    <div style={styles.wrap}>
      <h3 className="ae-embedded-title" style={styles.title}>🔒 {t.settingsTitle}</h3>
      <p style={styles.intro}>{t.settingsIntro}</p>

      {/* Stated before anything else is touched. Configuring a deposit that can never be taken is
          worse than not offering the setting at all — it looks switched on and quietly is not. */}
      {!settings.paymentsReady && (
        <div style={styles.warn}>
          ⚠️ {t.noStripe} <b>{t.noStripeFix}</b>
        </div>
      )}

      {/* Both preconditions are checked here rather than left to the server to refuse. A tick that
          is accepted and then rejected teaches nothing about what is missing. */}
      <label style={{ ...styles.toggleRow, cursor: canEnable || settings.enabled ? 'pointer' : 'default' }}>
        <input
          type="checkbox"
          checked={settings.enabled}
          disabled={saving || (!settings.enabled && !canEnable)}
          onChange={e => patch({ enabled: e.target.checked })}
          style={styles.checkbox}
        />
        <span>
          <span style={styles.toggleName}>{t.enabledLabel}</span>
          <span style={styles.hint}>
            {!settings.paymentsReady ? t.noStripeFix
              : needsAmount ? t.needAmount
              : t.enabledHint}
          </span>
        </span>
      </label>

      {/* Always visible, and above the rest on purpose. The amount is the precondition for
          switching deposits on at all — hiding it behind the toggle produced a dead end where
          ticking the box said "set an amount first" and there was nowhere to set one. */}
      <div style={styles.numbers}>
        <label style={styles.field}>
          <span style={styles.label}>{t.amountLabel}</span>
          <span style={styles.inputRow}>
            <input
              type="number" min={0} step={500} disabled={saving}
              value={settings.amount ?? ''}
              onChange={e => setSettings({ ...settings, amount: e.target.value === '' ? null : Number(e.target.value) })}
              onBlur={e => e.target.value !== '' && patch({ amount: Number(e.target.value) })}
              style={styles.input}
            />
            <span style={styles.unit}>{settings.currency}</span>
          </span>
          <span style={styles.hint}>{t.amountHint}</span>
        </label>
      </div>

      {settings.enabled && (
        <AdvancedDetails title={advancedTitle} count={3}>
          <div style={styles.numbers}>
            <label style={styles.field}>
              <span style={styles.label}>{t.leadLabel}</span>
              <span style={styles.inputRow}>
                <input
                  type="number" min={0} max={5} disabled={saving}
                  value={settings.holdLeadDays}
                  onChange={e => setSettings({ ...settings, holdLeadDays: Number(e.target.value) })}
                  onBlur={e => patch({ holdLeadDays: Number(e.target.value) })}
                  style={styles.input}
                />
              </span>
              <span style={styles.hint}>{t.leadHint}</span>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>{t.decisionLabel}</span>
              <span style={styles.inputRow}>
                <input
                  type="number" min={0} max={7} disabled={saving}
                  value={settings.decisionDays}
                  onChange={e => setSettings({ ...settings, decisionDays: Number(e.target.value) })}
                  onBlur={e => patch({ decisionDays: Number(e.target.value) })}
                  style={styles.input}
                />
              </span>
              <span style={styles.hint}>{t.decisionHint}</span>
            </label>
          </div>

          <div style={styles.label}>{t.autoLabel}</div>
          <div style={styles.modes}>
            {[false, true].map(auto => {
              const on = settings.autoRelease === auto;
              return (
                <button
                  key={String(auto)} type="button" disabled={saving}
                  onClick={() => patch({ autoRelease: auto })}
                  style={{ ...styles.mode, ...(on ? styles.modeOn : {}) }}
                >
                  <span style={styles.modeName}>{auto ? t.autoOn : t.autoOff}</span>
                  <span style={styles.modeHint}>{auto ? t.autoOnHint : t.autoOffHint}</span>
                </button>
              );
            })}
          </div>

          {settings.amount !== null && settings.amount > 0 && (
            <p style={styles.summary}>
              {money(settings.amount)} · {t.leadLabel.toLowerCase()} {settings.holdLeadDays}d ·{' '}
              {settings.decisionDays}d {t.needsDecision.toLowerCase()}
            </p>
          )}
        </AdvancedDetails>
      )}

      {saved && <div style={styles.saved}>{t.saved}</div>}
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { marginTop: 8 },
  title: { fontSize: 16, fontWeight: 700, margin: '0 0 4px' },
  intro: { fontSize: 13, color: '#64748b', margin: '0 0 16px' },
  warn: {
    padding: '10px 13px', borderRadius: 9, background: '#fffbeb',
    border: '1px solid #fcd34d', color: '#92400e', fontSize: 13, marginBottom: 16, lineHeight: 1.5,
  },
  toggleRow: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 18, cursor: 'pointer' },
  checkbox: { width: 17, height: 17, marginTop: 2, flex: 'none', cursor: 'pointer' },
  toggleName: { display: 'block', fontWeight: 700, fontSize: 14, color: '#1e293b' },
  label: {
    display: 'block', fontSize: 12, fontWeight: 700, letterSpacing: '.05em',
    textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6,
  },
  numbers: { display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', marginBottom: 20 },
  field: { display: 'block' },
  inputRow: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    width: 110, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
    fontSize: 14, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
  },
  unit: { fontSize: 12, color: '#64748b' },
  hint: { display: 'block', fontSize: 12, color: '#94a3b8', marginTop: 6, lineHeight: 1.45 },
  modes: { display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', marginBottom: 14 },
  mode: {
    display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left',
    padding: '11px 14px', borderRadius: 10, border: '1px solid #e2e8f0',
    background: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  modeOn: { borderColor: '#2563eb', borderWidth: 2, background: '#eff6ff' },
  modeName: { fontWeight: 700, fontSize: 14 },
  modeHint: { fontSize: 12, color: '#64748b', lineHeight: 1.4 },
  summary: {
    fontSize: 12.5, color: '#64748b', margin: 0,
    padding: '9px 12px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0',
  },
  saved: { fontSize: 13, color: '#16a34a', marginTop: 8 },
  error: { padding: '9px 12px', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 13, marginTop: 8 },
};

export default DepositSettings;
